import { PortedCartItem, PortedCategory } from './types';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * REGROUPEMENT PAR CATÉGORIE — version NEUTRE pour les modules portés
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ NE REMPLACE PAS `utils/categoryGrouper.ts` de la borne : celui-ci lit
 * `item.menuItem.category` et renvoie un objet SANS `sequence`, et il est utilisé par
 * les formateurs historiques. On ne le touche pas.
 *
 * Ici, la catégorie est déjà résolue dans `item.categoryId` par `adapter.ts`, et le
 * tri suit `category.sequence` (borne : `sequence_number`), comme côté console. Les
 * articles sans catégorie connue tombent dans un groupe de fin, jamais perdus.
 */

export interface PortedGroupedItems {
  categoryId: string;
  categoryName: string;
  sequence: number;
  items: PortedCartItem[];
}

const UNCATEGORIZED = 'uncategorized';

export const groupItemsByCategory = (
  items: PortedCartItem[],
  categories: PortedCategory[]
): PortedGroupedItems[] => {
  const categoryMap = new Map<string, PortedCategory>();
  for (const cat of categories || []) {
    if (cat && cat._id) categoryMap.set(cat._id, cat);
  }

  const grouped = new Map<string, PortedCartItem[]>();
  for (const item of items || []) {
    const categoryId = item.categoryId || UNCATEGORIZED;
    if (!grouped.has(categoryId)) grouped.set(categoryId, []);
    grouped.get(categoryId)!.push(item);
  }

  const result: PortedGroupedItems[] = [];
  grouped.forEach((groupItems, categoryId) => {
    const category = categoryMap.get(categoryId);
    const categoryName =
      (category?.name && String(category.name)) ||
      groupItems[0]?.categoryName ||
      'Autres';
    // 999 : les catégories inconnues passent en DERNIER, elles ne s'intercalent pas.
    const sequence = Number.isFinite(Number(category?.sequence))
      ? Number(category?.sequence)
      : 999;

    result.push({ categoryId, categoryName, sequence, items: groupItems });
  });

  result.sort((a, b) => a.sequence - b.sequence);
  return result;
};

/**
 * Agrège les options IDENTIQUES d'un article (« Extra fromage » choisi 2 fois → « 2 »).
 *
 * ⚠️ Clé `nom-prix` et NON `id-nom-prix` comme `utils/ticketFormatter.ts` de la borne :
 * deux options de même nom et même prix venant de deux groupes de spécifications
 * différents doivent être fusionnées sur le ticket, sinon la ligne apparaît deux fois.
 */
export const aggregateCustomizationOptions = (
  options: Array<{ name: string; price: number }>
): Array<{ name: string; price: number; quantity: number }> => {
  const aggregated = new Map<string, { name: string; price: number; quantity: number }>();

  for (const opt of options || []) {
    if (!opt) continue;
    const key = `${opt.name}-${opt.price}`;
    const existing = aggregated.get(key);
    if (existing) existing.quantity += 1;
    else aggregated.set(key, { name: opt.name, price: Number(opt.price) || 0, quantity: 1 });
  }

  return Array.from(aggregated.values());
};
